import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { PaginatedResponse, ApiResponse } from '../types';
import AuthService from './authService';
import { handleNonAuthError, type NormalizedApiError } from './errorInterceptor';

export type { NormalizedApiError };

// Create axios instance with default configuration
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 60000,
  // Send the httpOnly refresh cookie (dris_rt, scoped to /api/auth) on same-site XHR so /auth/refresh
  // and /auth/logout receive it. Credentialed CORS requires an explicit origin allowlist (no wildcard)
  // and AllowCredentials() on the API — both are configured server-side. The X-DRIS-Refresh header the
  // refresh call sends (see authService) forces a CORS preflight, blocking forged cross-origin refreshes.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Session expiry callback - registered by AuthContext
let onSessionExpired: (() => void) | null = null;

export const setSessionExpiredHandler = (handler: () => void) => {
  onSessionExpired = handler;
};

// Impersonation-expired callback - registered by AuthContext. Fired when a request 401s WHILE
// impersonating: instead of refreshing (which would mint an admin token and convert the session), the
// SPA fails safe by ending impersonation and restoring the admin session. See the 401 handler below.
let onImpersonationExpired: (() => void) | null = null;

export const setImpersonationExpiredHandler = (handler: () => void) => {
  onImpersonationExpired = handler;
};

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token and check if refresh needed
apiClient.interceptors.request.use(
  async (config) => {
    // Check if token needs refresh before making request. shouldRefreshToken() already returns false
    // while impersonating (C1) — the impersonation token is non-refreshable — so this proactive path
    // never fires an admin-cookie refresh mid-impersonation.
    if (AuthService.shouldRefreshToken() && !isRefreshing && !config.url?.includes('/auth/refresh')) {
      isRefreshing = true;
      try {
        // Tri-state; proactive path never logs out on failure. On a transient/invalid outcome we
        // simply proceed with the current token and let the response interceptor deal with any 401.
        await AuthService.refreshAccessToken();
      } catch {
        // refreshAccessToken no longer throws; kept defensive so a stray rejection can't wedge the flag.
      } finally {
        isRefreshing = false;
        // Drain any requests that queued (in the response interceptor) while this proactive refresh
        // held isRefreshing — otherwise they hang until timeout. Fixed here because the proactive
        // block previously set isRefreshing but never called processQueue. Draining with the current
        // token retries them: fresh token on success, old token otherwise (they re-enter 401 handling).
        processQueue(null, AuthService.getToken());
      }
    }

    const token = AuthService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // A 401 that carries a business errorCode envelope (e.g. OTP_PROOF_EXPIRED on the anonymous booking
    // flow) is NOT an expired-session challenge — refreshing the token can't fix it and would fire the
    // disruptive session-expiry side effects (clearAuthData / onSessionExpired) on an anonymous viewer.
    // Route it through the normal envelope-normalizer so its errorCode surfaces to the caller.
    const businessErrorCode = (error.response?.data as { errorCode?: unknown } | undefined)?.errorCode;

    // Check if error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry && !businessErrorCode) {

      // Don't retry refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        AuthService.clearAuthData();
        onSessionExpired?.();
        return Promise.reject(error);
      }

      // C1 FAIL-SAFE: a 401 WHILE impersonating means the impersonation token expired (or a refresh was
      // server-refused). NEVER refresh here — that would mint an ADMIN token off the admin cookie and
      // silently convert the session. End impersonation and restore the admin session instead, then
      // reject this request. (This branch precedes any refresh, and impersonation suppresses proactive
      // refresh, so the admin cookie is never used to clobber the impersonation identity.)
      if (AuthService.isImpersonating()) {
        onImpersonationExpired?.();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const outcome = await AuthService.refreshAccessToken();

        if (outcome === 'refreshed') {
          const token = AuthService.getToken();
          processQueue(null, token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        }

        if (outcome === 'transient') {
          // Transient refresh failure (503/5xx/429/network survived retries). Do NOT clear auth or
          // fire onSessionExpired — the session is still valid. Reject THIS request so the caller
          // sees the error, but keep the user signed in so a later request can recover.
          processQueue(error, null);
          return Promise.reject(error);
        }

        // 'invalid' — the refresh token is definitively dead. Log out.
        processQueue(error, null);
        AuthService.clearAuthData();
        onSessionExpired?.();
        return Promise.reject(error);
      } catch (refreshError) {
        // refreshAccessToken no longer throws; be conservative and treat an unexpected rejection as
        // transient (retain auth) rather than logging the user out on it.
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return handleNonAuthError(error);
  }
);

// Generic API methods
export const apiService = {
  // GET request with optional AbortSignal for cancellation
  get: async <T>(url: string, params?: unknown, signal?: AbortSignal): Promise<ApiResponse<T>> => {
    const response = await apiClient.get<ApiResponse<T>>(url, { params, signal });
    return response.data;
  },

  // POST request with optional config (e.g. timeout, headers, signal)
  post: async <T>(url: string, data?: unknown, config?: { timeout?: number; headers?: Record<string, string>; signal?: AbortSignal }): Promise<ApiResponse<T>> => {
    const response = await apiClient.post<ApiResponse<T>>(url, data, config);
    return response.data;
  },

  // PUT request
  put: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    const response = await apiClient.put<ApiResponse<T>>(url, data);
    return response.data;
  },

  // PATCH request
  patch: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    const response = await apiClient.patch<ApiResponse<T>>(url, data);
    return response.data;
  },

  // DELETE request
  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    const response = await apiClient.delete<ApiResponse<T>>(url);
    return response.data;
  },

  // Paginated GET request with optional AbortSignal for cancellation
  getPaginated: async <T>(url: string, params?: unknown, signal?: AbortSignal): Promise<PaginatedResponse<T>> => {
    const response = await apiClient.get<PaginatedResponse<T>>(url, { params, signal });
    return response.data;
  },
};

export default apiClient;
